import { motion } from "framer-motion";
import { Mail, ArrowRight, Globe } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";

const STATS = [
  { value: "30+", label: "Years in the Egyptian market" },
  { value: "ExxonMobil", label: "Registered contractor & vendor" },
  { value: "Global", label: "International sourcing network" },
];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#FBF9F5]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 font-mono text-xs font-medium tracking-[0.2em] uppercase text-amber-600"
            >
              <Globe className="h-4 w-4" /> Supplier · Importer · Sourcing Partner
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#0F172A]"
            >
              Connecting Global Brands with the Egyptian Market for Over 30
              Years
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              With more than 30 years of experience, Top Service is a trusted
              supplier, importer, and sourcing partner for major industrial and
              commercial organizations in the Egyptian market.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href={SITE.mailto}
                data-testid="hero-email-us-button"
                className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold text-[#0B2545] shadow-lg shadow-amber-500/25 transition-all duration-200 hover:bg-amber-400 hover:-translate-y-0.5 active:scale-95 focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
              >
                <Mail className="h-4 w-4" /> Email Us Directly
              </a>
              <a
                href="#services"
                data-testid="hero-explore-services-button"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-[#0B2545] transition-all duration-200 hover:border-[#0B2545] hover:-translate-y-0.5 active:scale-95"
              >
                Explore Our Services <ArrowRight className="h-4 w-4" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
            >
              {STATS.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.value}`}>
                  <p className="font-heading text-xl sm:text-2xl font-bold text-[#0B2545]">
                    {s.value}
                  </p>
                  <p className="mt-1 text-xs text-slate-500 leading-snug">
                    {s.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-900/5">
              <img
                src={IMAGES.heroShip}
                alt="Cargo ship docked at a maritime import port"
                className="w-full h-[360px] sm:h-[460px] lg:h-[540px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545]/40 via-transparent to-transparent" />
            </div>
            <div className="hidden sm:block absolute -bottom-6 -left-6 w-44 h-44 rounded-xl overflow-hidden shadow-xl ring-4 ring-[#FBF9F5]">
              <img
                src={IMAGES.heroContainers}
                alt="Stacked shipping containers at port"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
